import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { Alert, Button } from 'antd'

import { Modal } from 'common/components/Modal'
import { useSites } from 'common/hooks/useSites'
import type { SeriesTemplate } from 'features/Series/api/series.types'
import { SERIES_PAGES_TEXT } from 'features/Series/pages/shared/seriesPagesText'

import {
  BLANK_DRAFT,
  draftFromTemplate,
  templateFooterNote,
  type SeriesTemplateDraft,
} from '../templateForm.model'
import {
  ANY_FACILITY,
  NO_CATEGORY,
  SITE_FACILITY,
  TemplateFields,
  type CategoryValue,
  type FacilityValue,
  type SelectOption,
} from './TemplateFields'
import styles from './templateModal.module.scss'
import { useTemplateCategories, type TemplateCategoryOption } from './useTemplateCategories'
import { useTemplateSave } from './useTemplateSave'

const TEXT = SERIES_PAGES_TEXT.modal
const MODAL_WIDTH = 820
const NEW_KEY = 'new'

type Props = { open: boolean; template: SeriesTemplate | null; onClose: () => void }

type DialogProps = {
  template: SeriesTemplate | null
  onClose: () => void
  onBusyChange: (busy: boolean) => void
}

// Edit keeps the template's own category listed even when today's list lacks it.
const toCategoryOptions = (
  options: TemplateCategoryOption[],
  own: SeriesTemplate['category'],
): SelectOption<CategoryValue>[] => {
  const listed = own && !options.some((o) => o.id === own.id) ? [...options, own] : options
  return [
    { value: NO_CATEGORY, label: TEXT.categoryNone },
    ...listed.map((o) => ({ value: o.id, label: o.name })),
  ]
}

// Add: "Any" and this site. Edit: the template's own value and "Any".
const toFacilityOptions = (
  template: SeriesTemplate | null,
  siteName: string | undefined,
): SelectOption<FacilityValue>[] => {
  const any = { value: ANY_FACILITY, label: TEXT.facilityAny } as const
  if (template) {
    return template.facility
      ? [{ value: SITE_FACILITY, label: template.facility.name }, any]
      : [any]
  }
  return siteName ? [any, { value: SITE_FACILITY, label: siteName }] : [any]
}

const TemplateDialog = ({ template, onClose, onBusyChange }: DialogProps) => {
  const { siteId = '' } = useParams()
  const { selectedSite } = useSites()
  const categories = useTemplateCategories(siteId)
  const [draft, setDraft] = useState<SeriesTemplateDraft>(() =>
    template ? draftFromTemplate(template) : BLANK_DRAFT,
  )
  const { save, saveAsNew, isSaving, errors } = useTemplateSave({
    siteId,
    template,
    onDone: onClose,
  })

  useEffect(() => onBusyChange(isSaving), [isSaving, onBusyChange])

  const categoryOptions = useMemo(
    () => toCategoryOptions(categories.options, template?.category ?? null),
    [categories.options, template?.category],
  )
  const facilityOptions = useMemo(
    () => toFacilityOptions(template, selectedSite?.name),
    [template, selectedSite?.name],
  )

  const canSave = draft.name.trim() !== '' && !isSaving
  const isEdit = template !== null

  return (
    <div className={styles.shell}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>{isEdit ? TEXT.titleEdit : TEXT.titleAdd}</h2>
          {isEdit && template.inUse > 0 && (
            <span className={styles.badge}>{TEXT.inUseBadge(template.inUse)}</span>
          )}
        </div>
        {isEdit && <span className={styles.shared}>{TEXT.sharedLine}</span>}
      </div>
      {errors.other.length > 0 && (
        <Alert className={styles.alert} type='error' showIcon message={errors.other.join(' ')} />
      )}
      <TemplateFields
        draft={draft}
        onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        errors={errors.fields}
        disabled={isSaving}
        siteId={siteId}
        categoryOptions={categoryOptions}
        categoriesLoading={categories.isLoading}
        facilityOptions={facilityOptions}
      />
      <div className={styles.footer}>
        <span className={styles.note} data-testid='template-footer-note'>
          {templateFooterNote(draft)}
        </span>
        <span className={styles.spacer} />
        <Button className={styles.button} disabled={isSaving} onClick={onClose}>
          {TEXT.cancel}
        </Button>
        <Button
          className={`${styles.button} ${styles.primary}`}
          disabled={!canSave}
          loading={isSaving}
          onClick={() => save(draft)}>
          {TEXT.save}
        </Button>
        {isEdit && (
          <Button className={styles.button} disabled={!canSave} onClick={() => saveAsNew(draft)}>
            {TEXT.saveAsNew}
          </Button>
        )}
      </div>
    </div>
  )
}

export const TemplateModal = ({ open, template, onClose }: Props) => {
  // Keeps the last template while the modal fades out, so the title does not flip.
  const [held, setHeld] = useState(template)
  if (open && held !== template) setHeld(template)
  const shown = open ? template : held
  const [locked, setLocked] = useState(false)

  return (
    <Modal
      open={open}
      footer={null}
      width={MODAL_WIDTH}
      destroyOnHidden
      closable={!locked}
      maskClosable={false}
      aria-label={shown ? TEXT.titleEdit : TEXT.titleAdd}
      wrapClassName={styles.root}
      classNames={{ content: styles.content }}
      onCancel={() => {
        if (!locked) onClose()
      }}>
      <TemplateDialog
        key={shown?.id ?? NEW_KEY}
        template={shown}
        onClose={onClose}
        onBusyChange={setLocked}
      />
    </Modal>
  )
}
